import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { Field, reduxForm } from 'redux-form';
import { v4 } from 'uuid';
import SVG from 'react-inlinesvg';
import starIcon from '@lifesize/ux-assets/vectors/svgIcons/star.svg';
import starOutlineIcon from '@lifesize/ux-assets/vectors/svgIcons/star-outline.svg';
import { ONE_TIME_MEETING_FORM } from 'constants/ScheduleModal';
import BoxInput from 'components/ModalInput/BoxInput';
import ModeratorSearch from 'containers/ModeratorSearch/ModeratorSearchContainer';
import OneTimeMeetingButtons from 'components/OneTimeMeetingBuilder/OneTimeMeetingButtons/OneTimeMeetingButtons';
import classes from './OneTimeMeetingBuilder.module.scss';
import {
  validatePasscode,
  validateRequiredPasscode,
} from 'utils/meeting';

const autofillBuster = v4();

const messages = defineMessages({
  namePlaceholder: { id: 'components.contacts.oneTime.meetingName', defaultMessage: 'Enter a meeting name' },
});

class OneTimeMeetingBuilder extends React.Component {
  validateModerator = () => {
    if (!this.props.moderator) {
      return (
        <FormattedMessage
          id="components.contacts.meetingModal.moderatorRequiredError"
          defaultMessage="A moderator is required."
        />);
    }

    return undefined;
  };

  validateName = (value, values, props) => {
    const emptyValue = !value || (value && value.trim().length === 0);
    if (emptyValue && !this.props.contactFormError) {
      return (
        <FormattedMessage
          id="components.contacts.meetingModal.nameRequiredError"
          defaultMessage="A meeting name is required."
        />);
    } else if (!emptyValue && !!props.existingMeetingName) {
      if (props.existingMeetingName === value) {
        // Re-display the existing name warning,
        // so the user doesn't do a round trip for backend validation failure
        this.props.dispatchExistingNameError(value);
      }
      else {
        // Remove the warning message - user is entering a new meeting name
        this.props.resetErrorState();
      }
    }
    else if (!emptyValue) {
      // Remove the warning message - the form is clean
      this.props.resetErrorState();
    }
    return undefined;
  };

  prepSaveContact = (formValues = {}) => {
    const mappedValues = {};
    if (formValues.favorite) {
      mappedValues.favorite = formValues.favorite;
    }
    mappedValues.name = formValues[`${this.props.nameFieldName}-${autofillBuster}`];
    mappedValues.moderator = this.props.moderator;
    mappedValues.pin = formValues[`${this.props.passcodeFieldName}-${autofillBuster}`];
    return this.props.saveContact(mappedValues);
  };

  render() {
    const {
      moderator,
      intl,
      handleSubmit,
      nameFieldName,
      moderatorFieldName,
      oneTimeFavorite,
      contactFormError,
      passcodeFieldName,
      requireMeetingPasscode
    } = this.props;
    return (
      <div className={classes.oneTimeBuilderContainer}>
        <form
          onSubmit={handleSubmit(this.prepSaveContact)}
          data-testid="one-time-meet-form"
        >
          <dl>
            <div className={classes.detailGroup}>
              <dt />
              <dd>
                <h3 className={classes.oneTimeBuilderTitle}>
                  <FormattedMessage
                    id="oneTimeMeetingBuilder.title"
                    defaultMessage="New one-time meeting"
                  />
                </h3>
              </dd>
            </div>
            <div className={classes.detailGroup}>
              <dt>
                <FormattedMessage
                  id="oneTimeMeetingBuilder.name"
                  defaultMessage="Name*"
                />
              </dt>
              <dd>
                <Field
                  component={BoxInput}
                  dataTestId="one-time-meet-name"
                  label={intl.formatMessage(messages.namePlaceholder)}
                  maxlength={200}
                  name={`${nameFieldName}-${autofillBuster}`}
                  required
                  type="text"
                  validate={this.validateName}
                />
                {oneTimeFavorite && (
                  <label
                    className={classes.checkboxContainer}
                    data-testid="chkbox-favorite"
                  >
                    <Field
                      component="input"
                      id="createFavorite"
                      name="favorite"
                      type="checkbox"
                    />
                    <SVG src={starIcon} className={classes.checked} data-testid="starIcon" />
                    <SVG src={starOutlineIcon} className={classes.unchecked} data-testid="starOutlineIcon" />
                  </label>
                )}
              </dd>
            </div>
            <div className={classes.detailGroup}>
              <dt>
                <FormattedMessage
                  id="oneTimeMeetingBuilder.moderator"
                  defaultMessage="Moderator*"
                />
              </dt>
              <dd>
                <Field
                  boxType
                  component={ModeratorSearch}
                  listLength={5}
                  name={`${moderatorFieldName}-${autofillBuster}`}
                  passedName={`${moderatorFieldName}-${autofillBuster}`}
                  required
                  tooltipPosition="top"
                  type="hidden"
                  validate={this.validateModerator}
                  value={moderator}
                />
              </dd>
            </div>

            <div className={classes.detailGroup}>
              <dt>
                { requireMeetingPasscode ?
                  <FormattedMessage
                    id="oneTimeMeetingBuilder.numericPassCodeRequired"
                    defaultMessage="Numeric Passcode*"
                  />
                :
                  <FormattedMessage
                    id="oneTimeMeetingBuilder.numericPassCode"
                    defaultMessage="Numeric Passcode"
                  />
                }
              </dt>
              <dd>
                <Field
                  component={BoxInput}
                  dataTestId="one-time-meet-passcode"
                  label={intl.formatMessage(messages.namePlaceholder)}
                  maxlength={10}
                  name={`${passcodeFieldName}-${autofillBuster}`}
                  required={requireMeetingPasscode}
                  type="text"
                  validate={requireMeetingPasscode ? validateRequiredPasscode : validatePasscode}
                />
              </dd>
            </div>
          </dl>
          <div className={classes.requiredField}>
            <FormattedMessage
              id="oneTimeMeetingBuilder.required"
              defaultMessage="*Indicates required field"
            />
          </div>
          <OneTimeMeetingButtons
            {...this.props}
          >
            <div className={classes.errorReport}>
              {contactFormError}
            </div>
          </OneTimeMeetingButtons>
        </form>
      </div>
    );
  }
}

OneTimeMeetingBuilder.propTypes = {
  contactFormError: PT.string,
  handleSubmit: PT.func,
  intl: PT.object,
  moderator: PT.object,
  moderatorFieldName: PT.string,
  nameFieldName: PT.string,
  onBackClicked: PT.func,
  oneTimeFavorite: PT.bool,
  passcodeFieldName: PT.string,
  saveContact: PT.func,
  resetErrorState: PT.func,
  existingMeetingName: PT.string,
  dispatchExistingNameError: PT.func,
  requireMeetingPasscode: PT.bool
};

export default reduxForm({
  form: ONE_TIME_MEETING_FORM,
  onSubmitSuccess: (result, dispatch, props) => {
    props.handleSelectContact(result);
  },
  getFormState: state => state.app.form
})(injectIntl(OneTimeMeetingBuilder));
